import React, { useCallback } from 'react';

import { Box, Flex, Badge, rem, Paragraph } from '@vimeo/bokeh';
import { TrashBin, StopBanRight } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { BokehIconButton, BokehTooltip } from '@vimeo/live-core/components/bokeh';
import { LiveErrorBoundary } from '@vimeo/live-core/components/error/boundary';
import { IChatMessage, IChatUser } from '@vimeo/live-core/types/interaction_tools';
import { getTwoDigitFormat } from '@vimeo/live-core/utils/general/time';
import { OPACITY_FROM_HIDDEN_TO_VISIBLE_KEYFRAMES } from '@vimeo/live-core/utils/styling/keyframes';

import { createInteractionDomName } from '../../../../libs/shared/utils/dom/identifying';
import { translations } from '../../../../translations';
import { ChatMessageAuthor } from './ChatMessageAuthor';
import { ChatMessageContent } from './ChatMessageContent';

export interface IChatMessageProps extends IBaseDomElementProps {
  isPublic: boolean;
  message: IChatMessage;
  sender: IChatUser;
  isCurrentSender: boolean;
  // Whether current context viewer is creator level.
  isCreator: boolean;
  isBlocked: boolean;
  onDeleteMessage(messageId: string): void;
  onBlockUser?(senderId: string): void;
  onUnblockUser?(senderId: string): void;
}

export function ChatMessage({
  className = createInteractionDomName('chat-message'),
  isPublic,
  sender,
  message,
  isCurrentSender,
  isCreator,
  isBlocked,
  onDeleteMessage,
  onBlockUser,
  onUnblockUser,
}: IChatMessageProps) {
  const onDeleteMessageClick = useCallback(() => {
    onDeleteMessage(message.id);
  }, [onDeleteMessage, message?.id]);

  const onBlockUserClick = useCallback(() => {
    if (!onBlockUser) return;

    onBlockUser(sender.id);
  }, [sender.id, onBlockUser]);

  const onUnblockUserClick = useCallback(() => {
    if (!onUnblockUser) return;

    onUnblockUser(sender.id);
  }, [sender.id, onUnblockUser]);

  const { displayName } = sender;
  const { createdAt, message: messageText } = message;
  const senderName: string = displayName || translations.noName;
  // Users can delete own messages or if they have permissions (creators, mods).
  const canDeleteMessage: boolean = isCreator || isCurrentSender;
  // Users can block only in public chat if they have permissions + not own messages and not creator messages.
  const canBlockUser: boolean = isPublic && isCreator && !isCurrentSender && !sender.isCreator;
  const canManageMessage: boolean = canDeleteMessage || canBlockUser;

  return (
    <Flex
      data-group
      as={'li'}
      className={className}
      alignItems={'flex-start'}
      opacity={isBlocked ? 0.6 : 1}
      borderRadius={'sm'}
      padding={rem(8)}
      overflow={'hidden'}
      width={'100%'}
      maxWidth={'100%'}
      animation={`${OPACITY_FROM_HIDDEN_TO_VISIBLE_KEYFRAMES} 750ms`}
      _hover={{
        backgroundColor: 'fill-component-hover',
      }}
    >
      <Box position={'relative'} width={'100%'}>
        <Flex
          className={createInteractionDomName(className, 'author')}
          position={'relative'}
          alignItems={'center'}
          justifyContent={'flex-start'}
          margin={`0 0 ${rem(6)}`}
          gap={rem(8)}
          height={rem(24)}
        >
          <Flex
            className={createInteractionDomName(className, 'author-name')}
            alignItems={'center'}
            position={'relative'}
            width={0}
            flexGrow={1}
            gap={rem(8)}
          >
            <ChatMessageAuthor
              className={createInteractionDomName(className, 'author-name-label')}
              isCurrentSender={isCurrentSender}
              senderName={senderName}
            />

            {isCurrentSender ? (
              <Badge
                className={createInteractionDomName(className, 'author-name-badge-me')}
                size={'sm'}
                variant={'staff'}
                flexShrink={0}
              >
                {translations.me}
              </Badge>
            ) : null}

            {sender.isCreator ? (
              <Badge
                className={createInteractionDomName(className, 'author-name-badge-host')}
                size={'sm'}
                flexShrink={0}
              >
                {translations.host}
              </Badge>
            ) : null}
          </Flex>

          <Paragraph
            className={createInteractionDomName(className, 'time')}
            size={'sm'}
            whiteSpace={'nowrap'}
            display={'flex'}
            color={'text-secondary'}
            variant={'body-sm'}
            _groupHover={{
              visibility: 'hidden',
            }}
          >
            {getTwoDigitFormat(createdAt)}
          </Paragraph>

          {canManageMessage ? (
            <Flex
              display={'none'}
              justifyContent={'flex-end'}
              alignItems={'center'}
              position={'absolute'}
              color={'text-secondary'}
              zIndex={5}
              minWidth={rem(48)}
              height={rem(24)}
              top={0}
              right={0}
              _groupHover={{
                display: 'flex',
              }}
            >
              {canDeleteMessage ? (
                <BokehTooltip
                  placement={'top'}
                  modifiers={[
                    {
                      name: 'flip',
                      enabled: true,
                      options: {
                        fallbackPlacements: ['top-end'],
                      },
                    },
                  ]}
                  label={translations.deleteMessage}
                >
                  <BokehIconButton
                    className={createInteractionDomName(className, 'delete-button')}
                    size={'xs'}
                    icon={<TrashBin />}
                    iconWidth={rem(16)}
                    iconHeight={rem(16)}
                    height={rem(24)}
                    width={rem(24)}
                    padding={'0!important'}
                    onClick={onDeleteMessageClick}
                  />
                </BokehTooltip>
              ) : null}

              {canBlockUser ? (
                <BokehTooltip
                  placement={'top'}
                  modifiers={[
                    {
                      name: 'flip',
                      enabled: true,
                      options: {
                        fallbackPlacements: ['top-end'],
                      },
                    },
                  ]}
                  label={isBlocked ? translations.unblock : translations.blockUser}
                >
                  <BokehIconButton
                    className={createInteractionDomName(
                      className,
                      isBlocked ? 'unblock' : 'block',
                      'button',
                    )}
                    size={'xs'}
                    icon={<StopBanRight color={isBlocked ? 'red.500' : 'inherit'} />}
                    iconWidth={rem(16)}
                    iconHeight={rem(16)}
                    height={rem(24)}
                    width={rem(24)}
                    padding={'0!important'}
                    onClick={isBlocked ? onUnblockUserClick : onBlockUserClick}
                  />
                </BokehTooltip>
              ) : null}
            </Flex>
          ) : null}
        </Flex>

        <Flex flexDirection={'column'} flexGrow={1}>
          <LiveErrorBoundary component={'ChatMessageContent'} additionalInfo={{ message }}>
            <ChatMessageContent
              className={createInteractionDomName(className, 'content')}
              messageText={messageText}
            />
          </LiveErrorBoundary>
        </Flex>
      </Box>
    </Flex>
  );
}
